import { FC, Ref, forwardRef, PropsWithoutRef } from 'react';

import { Action, BaseProps, ForwardProps, NoChildrenProp } from '../../types';
import FormField from '../FormField';
import { FormControlProps, StyledFormControl } from '../FormControl';
import { hasProp } from '../../utils';
import { useUID } from '../../hooks';

import StyledInput from './Input.styles';

export interface InputProps extends FormControlProps, BaseProps, NoChildrenProp {
  /**
   * Specifies the type of input to be used.
   * @default "text"
   */
  type?:
    | 'text'
    | 'password'
    | 'number'
    | 'email'
    | 'url'
    | 'search'
    | 'tel'
    | 'date'
    | 'datetime-local'
    | 'time'
    | 'week'
    | 'month'
    | 'color';
  /** Pass an array of Action objects to append button(s) inline with the Input. */
  actions?: Action[];
}

const Input: FC<InputProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<InputProps>, ref: Ref<HTMLInputElement>) => {
    const uid = useUID();
    const {
      id = uid,
      value,
      defaultValue,
      required = false,
      disabled = false,
      label,
      labelHidden,
      info,
      status,
      actions,
      ...restProps
    } = props;

    const controlProp: {
      value?: string;
      defaultValue?: string;
    } = {};

    // Conditionally render component as controlled/uncontrolled
    if (hasProp(props, 'value')) {
      controlProp.value = value ?? '';
    } else if (hasProp(props, 'defaultValue')) {
      controlProp.defaultValue = defaultValue ?? '';
    }

    const Comp = (
      <StyledFormControl
        {...{
          ref,
          id,
          required,
          disabled,
          status,
          ...controlProp,
          ...restProps,
          as: StyledInput
        }}
      />
    );

    return label ? (
      <FormField {...{ label, labelHidden, id, info, status, required, disabled, actions }}>
        {Comp}
      </FormField>
    ) : (
      Comp
    );
  }
);

export default Input;
